// 📄 pages/Proyectos/ProgramasCatalogo.tsx
// Consulta (solo lectura) del Catálogo de Programas dentro de un proyecto —
// vive en /proyecto/:projectId/programas, dentro del grupo "Herramientas"
// del sidebar. El catálogo es global (no por proyecto, ver
// catalogoProgramasService.ts): el CRUD completo vive en /admin > Programas;
// acá cualquier consultor solo lo consulta/filtra/copia. Mismo patrón que
// BadiCatalogo.tsx.
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Code2, Info } from 'lucide-react';
import { Programa, getProgramas } from '../../services/catalogoProgramasService.ts';
import { ProgramaZ, getProgramasZ } from '../../services/programaZService.ts';
import { BadiObjeto, getBadis } from '../../services/catalogoBadisService.ts';
import { TransaccionSap, getTransacciones } from '../../services/catalogoTransaccionesService.ts';
import ProgramaCatalogGrid from '../../components/abap/ProgramaCatalogGrid.tsx';
import { useProject } from '../../context/ProjectContext.tsx';

type VistaProgramas = 'todas' | 'estandar' | 'z';

export default function ProgramasCatalogo() {
  const { projectId } = useProject();
  const [vista, setVista] = useState<VistaProgramas>('todas');
  const [programas, setProgramas] = useState<Programa[]>([]);
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [programasZ, setProgramasZ] = useState<ProgramaZ[]>([]);
  const [loadingZ, setLoadingZ] = useState(true);
  const [errorZ, setErrorZ] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getProgramas(), getBadis(), getTransacciones()])
      .then(([p, b, t]) => { setProgramas(p); setBadis(b); setTransacciones(t); })
      .catch((err) => {
        console.error('Error cargando el catálogo de Programas:', err);
        setError('No se pudo cargar el catálogo de Programas.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!projectId) { setLoadingZ(false); return; }
    setLoadingZ(true);
    getProgramasZ(projectId)
      .then(setProgramasZ)
      .catch((err) => {
        console.error('Error cargando los Programas Z del proyecto:', err);
        setErrorZ('No se pudieron cargar los Programas Z de este proyecto.');
      })
      .finally(() => setLoadingZ(false));
  }, [projectId]);

  // El grid espera el shape de Programa (ver ProgramaCatalogGrid.tsx).
  const programasZAdaptados = useMemo(
    () => programasZ.map((z) => ({
      id: z.id,
      nombre: z.nombre,
      nombreTecnico: z.nombreTecnico,
      solucion: z.solucion,
      descripcion: z.descripcion,
      badiRefIds: z.badiRefIds,
      transaccionRefIds: z.transaccionRefIds,
      responsable: z.responsable,
      sistemasDisponibles: z.sistemasDisponibles
    })),
    [programasZ]
  );

  const zCount = programasZ.length;

  const OPCIONES_VISTA: { id: VistaProgramas; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'estandar', label: 'Estándar' },
    { id: 'z', label: zCount > 0 ? `Programas Z (${zCount})` : 'Programas Z' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPCIONES_VISTA.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              vista === id
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {vista !== 'z' && (
        <>
          <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Cada programa puede estar relacionado con BAdIs/Objetos ABAP y Transacciones.</span>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          {loading ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando catálogo...</p>
            </div>
          ) : programas.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Code2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Todavía no hay programas registrados en el catálogo. Un Admin puede cargarlos desde /admin.</p>
            </div>
          ) : (
            <ProgramaCatalogGrid programas={programas} badis={badis} transacciones={transacciones} readOnly />
          )}
        </>
      )}

      {vista !== 'estandar' && projectId && (
        <div className={vista === 'todas' ? 'pt-6 border-t border-white/10 space-y-4' : 'space-y-4'}>
          <div>
            <h3 className="text-sm font-bold text-white">Programas Z de este proyecto</h3>
            <p className="text-xs text-gray-500 mt-0.5">Programas personalizados configurados para este proyecto. Solo tú y tu equipo los ven.</p>
          </div>

          {errorZ && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorZ}</div>
          )}

          {loadingZ ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando Programas Z...</p>
            </div>
          ) : programasZ.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Code2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Aún no hay Programas Z en este proyecto. Agrégalos en Configuración &gt; Desarrollo Z.</p>
            </div>
          ) : (
            <ProgramaCatalogGrid programas={programasZAdaptados} badis={badis} transacciones={transacciones} esZ readOnly />
          )}
        </div>
      )}
    </div>
  );
}
